import { useState, useEffect } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, User, InvestorProfile, db } from '../lib/supabase'

export function onAuthStateChange(callback: (session: Session | null) => void) {
  let currentSession: Session | null = null
  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    if (session?.user?.id === currentSession?.user?.id) return
    currentSession = session
    callback(session ?? null)
  })

  return () => subscription.unsubscribe()
}

export function useUser() {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<InvestorProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.user) {
          await loadUserData(session.user.id)
        } else {
          setLoading(false)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error loading session')
        setLoading(false)
      }
    }

    getSession()

    // Listen for auth changes using a small wrapper that avoids reloads
    const unsubscribe = onAuthStateChange(async (session) => {
      if (session?.user) {
        await loadUserData(session.user.id)
      } else {
        setUser(null)
        setProfile(null)
        setLoading(false)
      }
    })

    return unsubscribe
  }, [])

  const loadUserData = async (userId: string) => {
    try {
      setLoading(true)
      // Load user data
      const userData = await db.getUser(userId)
      setUser(userData)

      // Load investor profile if user is an investor
      if (userData.user_type === 'investor') {
        try {
          const profileData = await db.getInvestorProfile(userId)
          setProfile(profileData)
        } catch (profileError) {
          // Profile might not exist yet, create default one
        }
      }

      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading user data')
    } finally {
      setLoading(false)
    }
  }

  const updateUser = async (updates: Partial<User>) => {
    if (!user) return

    try {
      const updatedUser = await db.updateUser(user.id, updates)
      setUser(updatedUser)
      return updatedUser
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating user')
      throw err
    }
  }

  const updateProfile = async (updates: Partial<InvestorProfile>) => {
    if (!user) return

    try {
      const updatedProfile = await db.updateInvestorProfile(user.id, updates)
      setProfile(updatedProfile)
      return updatedProfile
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating profile')
      throw err
    }
  }

  const signOut = async () => {
    try {
      await supabase.auth.signOut()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error signing out')
    }
  }

  return {
    user,
    profile,
    loading,
    error,
    updateUser,
    updateProfile,
    signOut,
    isAuthenticated: !!user,
    isInvestor: user?.user_type === 'investor',
    isManager: user?.user_type === 'admin'
  }
}